import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  Sparkles, Wind, Box, Snowflake, Wifi, Zap, Volume2, Thermometer,
  Ruler, Weight, Droplets, Star, Power, Gauge, Leaf, ShieldCheck,
  Lightbulb, ChevronDown, FileText, Download,
} from "lucide-react";

interface SpecEntry { name: string; value: string }
interface Specifications {
  dimensions?: SpecEntry[];
  details?: SpecEntry[];
}
interface RelatedDoc { name: string; url: string }

interface Props {
  features?: string[] | null;
  specifications?: Specifications | null;
  showSpecifications?: boolean;
  relatedDocuments?: RelatedDoc[] | null;
}

/**
 * Maps a feature/spec key into a friendly lucide icon. Keep this list tight —
 * unknown keys fall back to Sparkles.
 */
function iconFor(text: string): JSX.Element {
  const t = text.toLowerCase();
  if (/btu|cool/.test(t)) return <Wind className="h-4 w-4" />;
  if (/capacit|cu\.?\s?ft|cubic|volume|liter/.test(t)) return <Box className="h-4 w-4" />;
  if (/ice|freez/.test(t)) return <Snowflake className="h-4 w-4" />;
  if (/wifi|wi[- ]?fi|smart|app|alexa|google/.test(t)) return <Wifi className="h-4 w-4" />;
  if (/watt|amp|volt|power|electric/.test(t)) return <Zap className="h-4 w-4" />;
  if (/decibel|db|noise|quiet|sound/.test(t)) return <Volume2 className="h-4 w-4" />;
  if (/temp|heat|degree/.test(t)) return <Thermometer className="h-4 w-4" />;
  if (/dimens|height|width|depth|inch|"|length/.test(t)) return <Ruler className="h-4 w-4" />;
  if (/weight|lbs|pound|kg/.test(t)) return <Weight className="h-4 w-4" />;
  if (/humid|water|moisture|drain|dehumid/.test(t)) return <Droplets className="h-4 w-4" />;
  if (/rating|star/.test(t)) return <Star className="h-4 w-4" />;
  if (/mode|setting|speed|fan/.test(t)) return <Gauge className="h-4 w-4" />;
  if (/energy|eco|efficient|green/.test(t)) return <Leaf className="h-4 w-4" />;
  if (/warranty|protect/.test(t)) return <ShieldCheck className="h-4 w-4" />;
  if (/light|led|display|screen/.test(t)) return <Lightbulb className="h-4 w-4" />;
  if (/power|button|on\/off/.test(t)) return <Power className="h-4 w-4" />;
  return <Sparkles className="h-4 w-4" />;
}

function FeaturesGrid({ features, max }: { features: string[]; max?: number }) {
  const shown = typeof max === "number" ? features.slice(0, max) : features;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {shown.map((f, i) => (
        <div
          key={i}
          className="flex items-start gap-2 p-2.5 rounded-md border bg-muted/30 hover:bg-muted/50 transition-colors"
          data-testid={`feature-item-${i}`}
        >
          <div className="text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0">{iconFor(f)}</div>
          <p className="text-sm leading-snug">{f}</p>
        </div>
      ))}
    </div>
  );
}

function SpecsList({ specs }: { specs: SpecEntry[] }) {
  return (
    <dl className="divide-y rounded-md border bg-card">
      {specs.map((s, i) => (
        <div
          key={i}
          className="grid grid-cols-[auto_1fr_auto] items-center gap-3 px-3 py-2 hover:bg-muted/30 transition-colors"
          data-testid={`spec-row-${i}`}
        >
          <div className="text-muted-foreground">{iconFor(s.name)}</div>
          <dt className="text-sm text-muted-foreground">{s.name}</dt>
          <dd className="text-sm font-semibold text-foreground text-right">{s.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function DocsList({ docs }: { docs: RelatedDoc[] }) {
  return (
    <div className="flex flex-col gap-1.5">
      {docs.map((doc, i) => (
        <div
          key={i}
          className="flex items-center gap-2 rounded-md border bg-secondary/30 hover:bg-secondary/60 transition-colors"
          data-testid={`document-row-${i}`}
        >
          <a
            href={doc.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center gap-2 px-3 py-2 text-sm font-medium text-foreground hover:underline min-w-0"
            data-testid={`document-link-${i}`}
          >
            <FileText className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
            <span className="truncate">{doc.name}</span>
          </a>
          <a
            href={doc.url}
            download={doc.name}
            className="flex items-center justify-center h-9 w-9 mr-1 rounded-md hover:bg-background text-muted-foreground hover:text-foreground transition-colors"
            title={`Download ${doc.name}`}
            aria-label={`Download ${doc.name}`}
            data-testid={`document-download-${i}`}
          >
            <Download className="h-4 w-4" />
          </a>
        </div>
      ))}
    </div>
  );
}

export function ProductFeaturesAndSpecs({ features, specifications, showSpecifications, relatedDocuments }: Props) {
  const hasFeatures = Array.isArray(features) && features.length > 0;
  const dims = showSpecifications ? specifications?.dimensions ?? [] : [];
  const dets = showSpecifications ? specifications?.details ?? [] : [];
  const hasSpecs = (dims.length + dets.length) > 0;
  const docs = Array.isArray(relatedDocuments) ? relatedDocuments.filter((d) => d?.url) : [];
  const hasDocs = docs.length > 0;
  const [showAllFeatures, setShowAllFeatures] = useState(false);

  if (!hasFeatures && !hasSpecs && !hasDocs) return null;

  // Tabbed layout whenever there are 2+ sections to show.
  const tabCount = (hasFeatures ? 1 : 0) + (hasSpecs ? 1 : 0) + (hasDocs ? 1 : 0);
  if (tabCount >= 2) {
    const defaultTab = hasFeatures ? "features" : hasSpecs ? "specs" : "docs";
    return (
      <Card className="mb-3 mt-3 overflow-hidden" data-testid="product-features-specs">
        <Tabs defaultValue={defaultTab}>
          <div className="border-b px-3 pt-3">
            <TabsList className="bg-transparent p-0 h-auto gap-1 flex-wrap">
              {hasFeatures && (
                <TabsTrigger value="features" data-testid="tab-features" className="data-[state=active]:bg-amber-50 dark:data-[state=active]:bg-amber-950/30 data-[state=active]:text-amber-700 dark:data-[state=active]:text-amber-300">
                  <Sparkles className="h-4 w-4 mr-1.5" /> Features
                </TabsTrigger>
              )}
              {hasSpecs && (
                <TabsTrigger value="specs" data-testid="tab-specs" className="data-[state=active]:bg-amber-50 dark:data-[state=active]:bg-amber-950/30 data-[state=active]:text-amber-700 dark:data-[state=active]:text-amber-300">
                  <Ruler className="h-4 w-4 mr-1.5" /> Specifications
                </TabsTrigger>
              )}
              {hasDocs && (
                <TabsTrigger value="docs" data-testid="tab-docs" className="data-[state=active]:bg-amber-50 dark:data-[state=active]:bg-amber-950/30 data-[state=active]:text-amber-700 dark:data-[state=active]:text-amber-300">
                  <FileText className="h-4 w-4 mr-1.5" /> Docs ({docs.length})
                </TabsTrigger>
              )}
            </TabsList>
          </div>
          {hasFeatures && (
            <TabsContent value="features" className="p-3 mt-0">
              <FeaturesGrid features={features!} max={showAllFeatures ? undefined : 6} />
              {features!.length > 6 && (
                <Button
                  variant="ghost" size="sm"
                  className="w-full mt-2 text-xs"
                  onClick={() => setShowAllFeatures((v) => !v)}
                  data-testid="button-toggle-features"
                >
                  <ChevronDown className={`h-3 w-3 mr-1 transition-transform ${showAllFeatures ? "rotate-180" : ""}`} />
                  {showAllFeatures ? "Show fewer" : `Show all ${features!.length} features`}
                </Button>
              )}
            </TabsContent>
          )}
          {hasDocs && (
            <TabsContent value="docs" className="p-3 mt-0">
              <DocsList docs={docs} />
            </TabsContent>
          )}
          {hasSpecs && (
          <TabsContent value="specs" className="p-3 mt-0 space-y-3">
            {/* Mobile: accordion. Desktop: side-by-side. */}
            <div className="md:hidden">
              <Accordion type="multiple" defaultValue={["dims", "dets"]}>
                {dims.length > 0 && (
                  <AccordionItem value="dims">
                    <AccordionTrigger className="text-sm">Dimensions ({dims.length})</AccordionTrigger>
                    <AccordionContent><SpecsList specs={dims} /></AccordionContent>
                  </AccordionItem>
                )}
                {dets.length > 0 && (
                  <AccordionItem value="dets">
                    <AccordionTrigger className="text-sm">Product Details ({dets.length})</AccordionTrigger>
                    <AccordionContent><SpecsList specs={dets} /></AccordionContent>
                  </AccordionItem>
                )}
              </Accordion>
            </div>
            <div className="hidden md:grid md:grid-cols-2 gap-3">
              {dims.length > 0 && (
                <div data-testid="specs-dimensions">
                  <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2 flex items-center gap-1.5">
                    <Ruler className="h-3 w-3" /> Dimensions
                  </h4>
                  <SpecsList specs={dims} />
                </div>
              )}
              {dets.length > 0 && (
                <div data-testid="specs-details">
                  <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2 flex items-center gap-1.5">
                    <Box className="h-3 w-3" /> Product Details
                  </h4>
                  <SpecsList specs={dets} />
                </div>
              )}
            </div>
          </TabsContent>
          )}
        </Tabs>
      </Card>
    );
  }

  // Single block (features OR specs OR docs)
  return (
    <Card className="mb-3 mt-3 overflow-hidden" data-testid="product-features-specs">
      <CardContent className="p-3 space-y-3">
        {hasFeatures && (
          <div>
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
              <Sparkles className="h-4 w-4 text-amber-500" /> Features & Highlights
            </h3>
            <FeaturesGrid features={features!} max={showAllFeatures ? undefined : 6} />
            {features!.length > 6 && (
              <Button variant="ghost" size="sm" className="w-full mt-2 text-xs"
                onClick={() => setShowAllFeatures((v) => !v)} data-testid="button-toggle-features">
                <ChevronDown className={`h-3 w-3 mr-1 transition-transform ${showAllFeatures ? "rotate-180" : ""}`} />
                {showAllFeatures ? "Show fewer" : `Show all ${features!.length} features`}
              </Button>
            )}
          </div>
        )}
        {hasSpecs && (
          <div className="grid md:grid-cols-2 gap-3">
            {dims.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2 flex items-center gap-1.5">
                  <Ruler className="h-3 w-3" /> Dimensions
                </h4>
                <SpecsList specs={dims} />
              </div>
            )}
            {dets.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2 flex items-center gap-1.5">
                  <Box className="h-3 w-3" /> Product Details
                </h4>
                <SpecsList specs={dets} />
              </div>
            )}
          </div>
        )}
        {hasDocs && (
          <div>
            <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2 flex items-center gap-1.5">
              <FileText className="h-3 w-3" /> Documents
            </h4>
            <DocsList docs={docs} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
